import { Effect, Layer, type Result, Schema as S, SchemaIssue } from "effect";
import {
	HttpClient,
	type HttpClientError,
	HttpClientRequest,
	type HttpClientResponse,
} from "effect/unstable/http";
import * as Command from "foldkit/command";
import * as Http from "foldkit/http";

import { ClerkTokenService } from "../auth/tokenService";
import { ApiError } from "./errors";
import { type Query, buildUrl } from "./url";

export const apiBase = (): string => import.meta.env.VITE_API_URL ?? "";

/** v1 strips a trailing `/api`: profile images are served from the origin root. */
export const imageBase = (): string => apiBase().replace(/\/api$/, "");

/** Runtime resources for the API layer (KTD4): fetch-backed HttpClient plus the token provider. */
export const apiLayer = (
	token: Layer.Layer<ClerkTokenService>,
): Layer.Layer<HttpClient.HttpClient | ClerkTokenService> =>
	Layer.merge(Http.layer, token);

export type ApiRequest = Readonly<{
	method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	path: string;
	query?: Query;
	/** JSON unless it is a `FormData`. */
	body?: unknown;
	/** `false` for public endpoints. */
	auth?: boolean;
}>;

export type Reader<T> = (
	response: HttpClientResponse.HttpClientResponse,
) => Effect.Effect<T, HttpClientError.HttpClientError | S.SchemaError>;

export const readJson =
	<Sch extends S.Codec<unknown, unknown, never, unknown>>(
		schema: Sch,
	): Reader<Sch["Type"]> =>
	(response) =>
		response.json.pipe(Effect.flatMap(S.decodeUnknownEffect(schema)));

const issuePath = (issue: SchemaIssue.Issue): string =>
	(SchemaIssue.makeFormatterStandardSchemaV1()(issue).issues[0]?.path ?? [])
		.map((segment) =>
			typeof segment === "object" ? String(segment.key) : String(segment),
		)
		.join(".");

const errorMessage = (body: unknown, fallback: string): string =>
	typeof body === "object" &&
	body !== null &&
	"error" in body &&
	typeof body.error === "string"
		? body.error
		: fallback;

/** v1's `throwIfLimitExceeded` condition, the AI opt-out, then 404, then everything else. Exported for the chat stream (KTD5). */
export const statusError = (status: number, body: unknown): ApiError => {
	const json = (
		typeof body === "object" && body !== null ? body : {}
	) as Record<string, unknown>;
	if (
		status === 429 &&
		json.error === "limit_exceeded" &&
		typeof json.metric === "string"
	) {
		return ApiError.LimitExceeded({
			metric: json.metric,
			current: typeof json.current === "number" ? json.current : 0,
			limit: typeof json.limit === "number" ? json.limit : 0,
			period: typeof json.period === "string" ? json.period : "",
		});
	}
	if (status === 403 && json.error === "ai_disabled") {
		return ApiError.AiDisabled();
	}
	if (status === 404) {
		return ApiError.NotFound({ message: errorMessage(body, "Not found") });
	}
	return ApiError.Http({
		status,
		message: errorMessage(body, "Unknown error"),
	});
};

const toHttpRequest = (
	request: ApiRequest,
	token: string | null,
): HttpClientRequest.HttpClientRequest => {
	const base = HttpClientRequest.make(request.method)(
		buildUrl(apiBase(), request.path, request.query),
		{ headers: token === null ? {} : { Authorization: `Bearer ${token}` } },
	);
	if (request.body instanceof FormData) {
		return HttpClientRequest.bodyFormData(base, request.body);
	}
	return request.body === undefined
		? base
		: HttpClientRequest.bodyJsonUnsafe(base, request.body);
};

/** Typed request: never rejects outside `ApiError`. A 401 is retried once with a re-read token (AE2). */
export const request = <T>(
	read: Reader<T>,
	req: ApiRequest,
): Effect.Effect<T, ApiError, HttpClient.HttpClient | ClerkTokenService> =>
	Effect.gen(function* () {
		const client = yield* HttpClient.HttpClient;
		const send = Effect.gen(function* () {
			const token =
				req.auth === false ? null : yield* (yield* ClerkTokenService).getToken;
			return yield* client.execute(toHttpRequest(req, token));
		});
		const first = yield* send;
		const response =
			first.status === 401 && req.auth !== false ? yield* send : first;
		if (response.status === 401) {
			return yield* Effect.fail(ApiError.Unauthenticated());
		}
		if (response.status >= 400) {
			const body = yield* response.json.pipe(Effect.orElseSucceed(() => ({})));
			return yield* Effect.fail(statusError(response.status, body));
		}
		return yield* read(response);
	}).pipe(
		Effect.catchTags({
			NoSession: () => Effect.fail(ApiError.Unauthenticated()),
			SchemaError: (error) =>
				Effect.fail(ApiError.Decode({ path: issuePath(error.issue) })),
			HttpClientError: (error) =>
				Effect.fail(
					error.reason._tag === "DecodeError"
						? ApiError.Decode({ path: "" })
						: ApiError.Transport({ message: error.message }),
				),
		}),
	);

type WithGeneration<
	Fields extends S.Struct.Fields,
	Extra extends S.Struct.Fields,
> = Fields &
	Extra & {
		generation: typeof S.Number;
	};
type Args<Fields extends S.Struct.Fields> = S.Schema.Type<S.Struct<Fields>>;
type Payload<
	Fields extends S.Struct.Fields,
	Extra extends S.Struct.Fields,
	T,
> = Args<WithGeneration<Fields, Extra>> & {
	readonly result: Result.Result<T, ApiError>;
};

/** Extra Command args pages tag every fetch with (KTD3): the `asOf` it was issued for. */
export const asOfTag = { asOf: S.Option(S.String) };

/**
 * One builder per v1 `api.ts` export. `command(Message, toMessage, extra?)` yields a
 * Foldkit Command whose args are `config.args` plus `extra` (pages pass `asOfTag`)
 * plus `generation` (KTD3), and whose Message carries the args and a
 * `Result<T, ApiError>`. Extra args are carried, never sent.
 */
export const defineApi = <
	const Name extends string,
	const Fields extends S.Struct.Fields,
	Res extends S.Codec<unknown, unknown, never, unknown>,
>(
	name: Name,
	config: Readonly<{
		args: Fields;
		response: Res;
		request: (args: Args<Fields>) => ApiRequest;
		read?: Reader<Res["Type"]>;
	}>,
) => {
	const read = config.read ?? readJson(config.response);
	const run = (
		args: Args<Fields>,
	): Effect.Effect<
		Result.Result<Res["Type"], ApiError>,
		never,
		HttpClient.HttpClient | ClerkTokenService
	> => request(read, config.request(args)).pipe(Effect.result);
	return {
		name,
		/** Schema for the Message field: `S.Result(response, ApiError)`. */
		Result: S.Result(config.response, ApiError),
		run,
		command: <
			Msg extends S.Top,
			const Extra extends S.Struct.Fields = Record<never, never>,
		>(
			message: Msg,
			...mapping:
				| readonly [
						toMessage: (
							payload: Payload<Fields, Extra, Res["Type"]>,
						) => S.Schema.Type<Msg>,
						extra?: Extra,
				  ]
				| (Msg extends (
						payload: Payload<Fields, Extra, Res["Type"]>,
				  ) => S.Schema.Type<Msg>
						? readonly [extra?: Extra]
						: never)
		) => {
			const [toMessageOrExtra, explicitExtra] = mapping;
			const identity = typeof toMessageOrExtra !== "function";
			const toMessage = (identity ? message : toMessageOrExtra) as (
				payload: Payload<Fields, Extra, Res["Type"]>,
			) => S.Schema.Type<Msg>;
			const extra = ((identity ? toMessageOrExtra : explicitExtra) ??
				{}) as Extra;
			const args: WithGeneration<Fields, Extra> = {
				...config.args,
				...extra,
				generation: S.Number,
			};
			return Command.define(name, {
				args,
				messages: [message],
				// `a` is `Args<Fields>` plus `generation`; TS cannot narrow the generic intersection back.
				execute: (a) =>
					run(a as Args<Fields>).pipe(
						Effect.map((result) => toMessage({ ...a, result })),
					),
			});
		},
	};
};

type ApiSchema = S.Codec<unknown, unknown, never, unknown>;
type StaticApiDefinition = readonly [
	method: ApiRequest["method"],
	path: string,
	response: ApiSchema,
	options?: Omit<ApiRequest, "method" | "path">,
];

type StaticApi<Name extends string, Response extends ApiSchema> = ReturnType<
	typeof defineApi<Name, Record<never, never>, Response>
>;
type StaticApis<Definitions extends Record<string, StaticApiDefinition>> = {
	readonly [Name in keyof Definitions]: StaticApi<
		Capitalize<Name & string>,
		Definitions[Name][2]
	>;
};

const commandName = <Name extends string>(name: Name): Capitalize<Name> =>
	`${name.charAt(0).toUpperCase()}${name.slice(1)}` as Capitalize<Name>;

/** API builders for endpoints whose request has no runtime arguments. */
export const staticApis = <
	const Definitions extends Record<string, StaticApiDefinition>,
>(
	definitions: Definitions,
): StaticApis<Definitions> =>
	Object.fromEntries(
		Object.entries(definitions).map(
			([name, [method, path, response, options]]) => [
				name,
				defineApi(commandName(name), {
					args: {},
					response,
					request: () => ({ ...options, method, path }),
				}),
			],
		),
	) as StaticApis<Definitions>;
